// WO-2026-09-14 Tracks A–C — "Preprint productivity": every preprint in the pipeline, grouped into
// the two drawn buckets, with per-card Change Status / Editorial Reject / ··· actions. Built to the
// Sep-14 revision of design repo docs/designs/2026-07-21-migration-daily-aaron-ted-angel/
// productivity-tab-v3.dc.html. Staff-only (server-gated on viewAuditTrail like the AE overview).
//
// Structure and hierarchy follow the canvas; chrome, labels and tones are the app's (`Badge`,
// `Modal`, preprintStatusLabel, statusTone — "In Queue", not DORMANT; no "Update record" title).
// Every action is hidden with useCan AND re-checked server-side:
//   Change Status  → PATCH /preprints/:id/status  (Central/Admin `activatePreprint`; the transition
//                    table still applies, so ACTIVE → In Queue stays disabled with the reason)
//   Editorial Reject → the same PATCH with DISCARDED + a mandatory reason (`forceStatus`) — the app
//                    has no post-activation "Editorial Reject"; the confirm copy says Discarded
//                    honestly until Angel settles the vocabulary (WO Track F)
//   Add Reviewer   → a link into the Reviewer Proposals portal with the preprint preselected
//   Send Invites   → POST /operations/:id/batch-csv — the existing 20-then-+10 Janeway CSV batch
//                    (Manager/Admin `sendJanewayInvites`); no threshold bypass (Track F)
// The grouping / sort / action rules live in preprint-buckets.ts so a unit test can pin them.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import {
  preprintStatusLabel,
  type PreprintProductivity as PreprintProductivityPayload,
  type PreprintProductivityRow,
  type PreprintStatus,
} from "@rrid/shared-types";
import { api, ApiError } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { doiHref } from "../../lib/doi.js";
import { flagForTeam } from "../../lib/teamFlags.js";
import { useCan } from "../../components/Can.js";
import { Badge, Button, Card, Field, Input, Modal, Select, Spinner, Textarea, statusTone } from "../../components/ui.js";
import {
  bucketRows,
  PREVIEW_ROWS,
  STATUS_CHOICES,
  rowActions,
  stageLabel,
  type RowActions,
} from "./preprint-buckets.js";

const fmt = (iso: string | null | undefined): string => (iso ? iso.slice(0, 10) : "—");
const rowKey = (r: PreprintProductivityRow): string => r.preprintId ?? r.selectedId ?? r.title;

type ModalState = { kind: "status" | "reject" | "invites"; row: PreprintProductivityRow } | null;

/** Everything a status write can move: this page, the finding list, details, ops, dashboards. */
function useInvalidatePipeline(): () => void {
  const qc = useQueryClient();
  return () => {
    for (const key of ["dashboard", "preprints", "preprint", "selected", "operations"]) {
      void qc.invalidateQueries({ queryKey: [key] });
    }
  };
}

function toggleIn(set: Set<string>, key: string): Set<string> {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

export function PreprintProductivity(): JSX.Element {
  const can = useCan();
  const [q, setQ] = useState("");
  const [tableQ, setTableQ] = useState<Record<string, string>>({});
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const [seeAll, setSeeAll] = useState<Set<string>>(new Set());
  const [openCards, setOpenCards] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<ModalState>(null);

  const { data, isLoading } = useQuery({
    queryKey: qk.productivityPreprints,
    queryFn: () => api.get<PreprintProductivityPayload>("/dashboard/preprints"),
  });
  const buckets = useMemo(() => bucketRows(data?.rows ?? [], q, tableQ), [data, q, tableQ]);

  if (isLoading || !data) return <Spinner label="Loading preprint productivity…" />;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Preprint productivity</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Every preprint in the pipeline — grouped by stage, with the team and AE shepherding it.
          </p>
        </div>
        <Input
          type="search"
          className="w-64 py-1"
          placeholder="Search titles, AEs, teams, domains…"
          aria-label="Search preprints"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </header>

      {buckets.length === 0 && (
        <p className="text-sm text-slate-500">
          {q.trim() ? (
            <>No preprints match &ldquo;{q.trim()}&rdquo;.</>
          ) : (
            "No preprints in the pipeline yet."
          )}
        </p>
      )}

      {buckets.map((b) => {
        const open = !closed.has(b.def.key);
        const expanded = seeAll.has(b.def.key);
        const shown = expanded ? b.rows : b.rows.slice(0, PREVIEW_ROWS);
        const countLabel = `${b.rows.length} preprint${b.rows.length === 1 ? "" : "s"}`;
        return (
          <Card key={b.def.key} className="overflow-visible">
            <button
              type="button"
              className="flex w-full flex-wrap items-center gap-2.5 border-b border-slate-100 px-4 py-2.5 text-left hover:bg-slate-50"
              aria-expanded={open}
              onClick={() => setClosed((cur) => toggleIn(cur, b.def.key))}
            >
              <Badge tone={b.def.tone}>{b.def.title}</Badge>
              <span className="min-w-[8rem] text-xs text-slate-500">
                {countLabel} · {b.def.stages.map(stageLabel).join(", ")}
              </span>
              <span className="flex flex-wrap gap-1.5">
                <Badge tone="blue">Proposed {b.proposed}</Badge>
                <Badge tone="slate">Unchecked {b.unchecked}</Badge>
                <Badge tone="violet">MiniTeam {b.miniTeam}</Badge>
              </span>
              <span className="ml-auto flex items-center gap-3">
                <span className="text-right">
                  <span className="block text-lg font-bold leading-tight text-green-700">{b.greenlits}</span>
                  <span className="block text-[10px] uppercase tracking-wide text-slate-500">Greenlits</span>
                </span>
                <span className="text-xs text-slate-400">{open ? "▲" : "▼"}</span>
              </span>
            </button>
            {open && (
              <>
                <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5">
                  <Input
                    type="search"
                    className="w-72 py-1"
                    placeholder="Search titles in this table…"
                    aria-label={`Search titles in ${b.def.title}`}
                    value={tableQ[b.def.key] ?? ""}
                    onChange={(e) => setTableQ((cur) => ({ ...cur, [b.def.key]: e.target.value }))}
                  />
                  <span className="text-xs text-slate-400">{countLabel}</span>
                </div>
                <div className="flex flex-col gap-2.5 px-4 pb-1 pt-3">
                  {shown.map((r) => {
                    const key = `${b.def.key}|${rowKey(r)}`;
                    return (
                      <PreprintCard
                        key={key}
                        row={r}
                        open={openCards.has(key)}
                        onToggle={() => setOpenCards((cur) => toggleIn(cur, key))}
                        actions={rowActions(r, can)}
                        onAction={(kind) => setModal({ kind, row: r })}
                      />
                    );
                  })}
                </div>
                {b.rows.length > PREVIEW_ROWS && (
                  <div className="flex justify-center border-t border-slate-100 px-4 py-2.5">
                    <Button variant="secondary" className="py-1" onClick={() => setSeeAll((cur) => toggleIn(cur, b.def.key))}>
                      {expanded ? "Show fewer" : `See all ${b.rows.length} preprints`}
                    </Button>
                  </div>
                )}
              </>
            )}
          </Card>
        );
      })}

      <p className="text-xs text-slate-400">
        Proposed counts exclude Student Reject and COI Detected. Selected / Activated dates come from the audit
        trail and show &ldquo;—&rdquo; for rows imported before the webapp recorded them. Reject rate flags
        above 50% once a preprint has enough proposals to judge.
      </p>

      {modal?.kind === "status" && <ChangeStatusModal row={modal.row} onClose={() => setModal(null)} />}
      {modal?.kind === "reject" && <EditorialRejectModal row={modal.row} onClose={() => setModal(null)} />}
      {modal?.kind === "invites" && <SendInvitesModal row={modal.row} onClose={() => setModal(null)} />}
    </div>
  );
}

// ── Card ─────────────────────────────────────────────────────────────────────
function PreprintCard({
  row,
  open,
  onToggle,
  actions,
  onAction,
}: {
  row: PreprintProductivityRow;
  open: boolean;
  onToggle: () => void;
  actions: RowActions;
  onAction: (kind: "status" | "reject" | "invites") => void;
}): JSX.Element {
  const hasMiniTeam = row.miniTeamCount > 0;
  const detailsHref = row.selectedId ? `/preprints/${row.selectedId}` : null;
  const menuItems: { label: string; to?: string; onSelect?: () => void }[] = [];
  if (actions.addReviewer && row.preprintId) {
    menuItems.push({ label: "Add Reviewer", to: `/reviewers?preprint=${row.preprintId}` });
  }
  if (actions.sendInvites) menuItems.push({ label: "Manually Send Invites", onSelect: () => onAction("invites") });
  const anyAction = actions.changeStatus || actions.editorialReject || menuItems.length > 0;

  return (
    <div className={clsx("rounded-lg border bg-white", open ? "border-brand" : "border-slate-200")}>
      <div
        className="flex cursor-pointer items-start gap-4 px-4 py-3 hover:bg-slate-50"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="text-[15px] font-medium leading-snug text-slate-900" style={{ textWrap: "pretty" }}>
            {detailsHref ? (
              <Link to={detailsHref} className="hover:text-brand hover:underline" onClick={(e) => e.stopPropagation()}>
                {row.title}
              </Link>
            ) : (
              row.title
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            {row.doi && (
              <a
                href={doiHref(row.doi)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex max-w-[280px] items-center gap-1 truncate text-[13px] text-blue-700 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <span aria-hidden>🔗</span>
                <span className="truncate">{row.doi}</span>
              </a>
            )}
            <Badge tone={statusTone(row.status)}>{stageLabel(row.status)}</Badge>
            <Badge tone="slate">{row.team ? `${flagForTeam(row.team)} ${row.team}` : "No team"}</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="slate">{row.domain ?? "No domain"}</Badge>
            <Badge tone={hasMiniTeam ? "violet" : "slate"}>
              {hasMiniTeam ? `MiniTeam: Yes · ${row.miniTeamCount}` : "MiniTeam: No"}
            </Badge>
            {row.gatesFunded && <Badge tone="amber">Gates Funded Author</Badge>}
            {row.highRejectRate && (
              <span title="More than half of proposed reviewers were rejected">
                <Badge tone="red">High reject rate · {row.rejectRatePct}%</Badge>
              </span>
            )}
            <span className="text-[13px] text-slate-500">Posted on server {fmt(row.publishedDate)}</span>
            <span className="text-[13px] text-slate-500">· Selected {fmt(row.selectedAt)}</span>
            <span className="text-[13px] text-slate-500">· Activated {fmt(row.activatedAt)}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {actions.changeStatus && (
            <Button onClick={() => onAction("status")}>Change Status</Button>
          )}
          {actions.editorialReject && (
            <Button variant="secondary" onClick={() => onAction("reject")}>
              Editorial Reject
            </Button>
          )}
          {menuItems.length > 0 && <RowMenu items={menuItems} />}
          {!anyAction && <span className="text-[11px] text-slate-400">No actions</span>}
          <span className="text-xs text-slate-400" aria-hidden>
            {open ? "▲" : "▼"}
          </span>
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-100 px-4 py-4">
          <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
            <Cell label="Invite Count" value={row.inviteCount} />
            <Cell label="Unsent Invites" value={row.unsentInvites == null ? <NotTracked /> : row.unsentInvites} />
            <Cell label="Total Unresolved" value={row.unresolvedCount} />
            <Cell label="AE Unchecked" value={row.uncheckedCount} />
            <Cell label="Fixable (+COI flagged)" value={`${row.fixableCount} (+${row.coiFlaggedCount} COI flagged)`} />
            <Cell label="Proposed" value={row.proposedCount} />
            <Cell label="Reject" value={row.rejectedCount} />
            <Cell label="Reject Rate" value={`${row.rejectRatePct}%`} />
            <Cell label="Lead AE" value={row.assignedEditorEmail ?? "—"} />
            <Cell label="Lead AE Name" value={row.assignedEditorName ?? "—"} />
            {/* WO-2026-09-14 Track F: `selected.graduate_assignee` is written by nothing — placeholder, not 0. */}
            <Cell label="Graduate Assignee" value={<NotTracked />} />
            {/* Track F: no "Student Pitcher" field exists; the Selected row's assignee is the student who pitched it. */}
            <Cell label="Student Pitcher" value={row.assigneeName ?? row.assigneeEmail ?? "—"} />
          </dl>
        </div>
      )}
    </div>
  );
}

function Cell({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-sm font-semibold text-slate-900">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-600">{value}</dd>
    </div>
  );
}

/** Field with no data source yet — the app's explicit placeholder beats an invented number. */
function NotTracked(): JSX.Element {
  return (
    <span className="text-slate-300" title="Not tracked in the webapp yet">
      —
    </span>
  );
}

/** The card's "···" menu. No kebab primitive exists in ui.tsx; click-outside closes it. */
function RowMenu({ items }: { items: { label: string; to?: string; onSelect?: () => void }[] }): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const itemClass = "block w-full rounded-md px-3 py-2 text-left text-sm text-slate-900 hover:bg-slate-100";
  return (
    <span ref={ref} className="relative inline-block">
      <Button variant="secondary" aria-haspopup="menu" aria-expanded={open} aria-label="More actions" onClick={() => setOpen((o) => !o)}>
        ···
      </Button>
      {open && (
        <span
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-30 flex min-w-56 flex-col rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
        >
          {items.map((it) =>
            it.to ? (
              <Link key={it.label} role="menuitem" to={it.to} className={itemClass} onClick={() => setOpen(false)}>
                {it.label}
              </Link>
            ) : (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                className={itemClass}
                onClick={() => {
                  setOpen(false);
                  it.onSelect?.();
                }}
              >
                {it.label}
              </button>
            ),
          )}
        </span>
      )}
    </span>
  );
}

// ── Modals ───────────────────────────────────────────────────────────────────
function errorText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function ChangeStatusModal({ row, onClose }: { row: PreprintProductivityRow; onClose: () => void }): JSX.Element {
  const targets = rowActions(row, () => true).statusTargets;
  const current = row.status as PreprintStatus;
  const [choice, setChoice] = useState<PreprintStatus>(current);
  const invalidate = useInvalidatePipeline();
  const update = useMutation({
    mutationFn: () => api.patch(`/preprints/${row.preprintId}/status`, { status: choice }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title="Change status"
      footer={
        <>
          <span className="flex-1 text-xs text-red-700">{update.isError && errorText(update.error, "Could not change the status.")}</span>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={choice === current || !targets.includes(choice) || update.isPending} onClick={() => update.mutate()}>
            {update.isPending ? "Updating…" : "Update"}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-slate-700">
        <span className="font-medium">{row.title}</span>
        <br />
        Currently <Badge tone={statusTone(current)}>{preprintStatusLabel(current)}</Badge>
      </p>
      <Field label="Preprint status" required>
        <Select className="w-full" value={choice} onChange={(e) => setChoice(e.target.value as PreprintStatus)}>
          {STATUS_CHOICES.map((s) => {
            const allowed = s === current || targets.includes(s);
            return (
              <option key={s} value={s} disabled={!allowed}>
                {preprintStatusLabel(s)}
                {s === current ? " (current)" : allowed ? "" : " — not a permitted move"}
              </option>
            );
          })}
        </Select>
      </Field>
      {current === "ACTIVE" && (
        <p className="mt-2 text-xs text-slate-500">
          An active preprint never goes back to In Queue (FR-P3); it can be put On Hold.
        </p>
      )}
    </Modal>
  );
}

function EditorialRejectModal({ row, onClose }: { row: PreprintProductivityRow; onClose: () => void }): JSX.Element {
  const [reason, setReason] = useState("");
  const invalidate = useInvalidatePipeline();
  const reject = useMutation({
    mutationFn: () => api.patch(`/preprints/${row.preprintId}/status`, { status: "DISCARDED", reason: reason.trim() }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title="Editorial Reject"
      footer={
        <>
          <span className="flex-1 text-xs text-red-700">{reject.isError && errorText(reject.error, "Could not reject this preprint.")}</span>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" disabled={!reason.trim() || reject.isPending} onClick={() => reject.mutate()}>
            {reject.isPending ? "Rejecting…" : "Yes, reject"}
          </Button>
        </>
      }
    >
      <p className="text-sm text-slate-900">
        Are you sure? Confirming moves this preprint to <Badge tone="red">{preprintStatusLabel("DISCARDED")}</Badge> —
        this cannot be undone.
      </p>
      <p className="mt-2 text-sm text-slate-500">{row.title}</p>
      <div className="mt-4">
        <Field label="Reason" required hint="Recorded in the audit trail with the rejection.">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this preprint being rejected?" />
        </Field>
      </div>
    </Modal>
  );
}

interface BatchResult {
  sent: boolean;
  greenlit: number;
  batch?: number;
}

function SendInvitesModal({ row, onClose }: { row: PreprintProductivityRow; onClose: () => void }): JSX.Element {
  const invalidate = useInvalidatePipeline();
  const send = useMutation({
    mutationFn: () => api.post<BatchResult>(`/operations/${row.preprintId}/batch-csv`),
    onSuccess: () => invalidate(),
  });
  const result = send.data;
  return (
    <Modal
      open
      onClose={onClose}
      title="Send Invites"
      footer={
        <>
          <span className="flex-1 text-xs text-red-700">{send.isError && errorText(send.error, "Could not send invites.")}</span>
          <Button variant="secondary" onClick={onClose}>
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button disabled={send.isPending} onClick={() => send.mutate()}>
              {send.isPending ? "Sending…" : "Send"}
            </Button>
          )}
        </>
      }
    >
      <p className="text-sm text-slate-900">
        Sends the next Janeway CSV batch for the <span className="font-semibold">{row.greenlitCount}</span> greenlit reviewer
        {row.greenlitCount === 1 ? "" : "s"} on this preprint.
      </p>
      <p className="mt-2 text-sm text-slate-500">{row.title}</p>
      <p className="mt-3 text-xs text-slate-500">
        Batches go out at 20 greenlit reviewers and every 10 after that; a batch that has already gone out is not
        resent, so this is safe to press twice.
      </p>
      {result && (
        <p className={clsx("mt-4 rounded-md px-3 py-2 text-sm", result.sent ? "bg-green-50 text-green-800" : "bg-slate-100 text-slate-700")}>
          {result.sent
            ? `Batch #${result.batch ?? "?"} sent — ${result.greenlit} greenlit reviewers.`
            : `No batch due yet — ${result.greenlit} greenlit reviewer${result.greenlit === 1 ? "" : "s"}${
                result.batch ? `; batch #${result.batch} already sent` : ""
              }.`}
        </p>
      )}
    </Modal>
  );
}
